/**
 * EnergyVisualizer Component
 * Reacts to chat activity and listener energy
 */

import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const EnergyVisualizer = ({ energyLevel = 50, isPlaying, isLive }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  useEffect(() => {
    const particleCount = 60;
    particlesRef.current = Array.from({ length: particleCount }, (_, i) => ({
      angle: (i / particleCount) * Math.PI * 2,
      radius: 100,
      speed: 0.02 + Math.random() * 0.02,
      amplitude: 10 + Math.random() * 20,
      phase: Math.random() * Math.PI * 2,
      size: 2 + Math.random() * 3
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    
    const baseColor = isLive ? [255, 0, 127] : [0, 240, 255];
    const secondaryColor = isLive ? [0, 240, 255] : [57, 255, 20];
    
    let time = 0;
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += 0.02;
      
      const energyFactor = energyLevel / 100;
      const playingFactor = isPlaying ? 1 : 0.3;
      
      // Outer glow
      const gradient = ctx.createRadialGradient(centerX, centerY, 80, centerX, centerY, 150 + energyFactor * 30);
      gradient.addColorStop(0, `rgba(${baseColor.join(',')}, 0)`);
      gradient.addColorStop(0.5, `rgba(${baseColor.join(',')}, ${0.1 * energyFactor * playingFactor})`);
      gradient.addColorStop(1, `rgba(${baseColor.join(',')}, 0)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Particles
      particlesRef.current.forEach((particle, i) => {
        particle.angle += particle.speed * playingFactor;
        const pulseRadius = particle.radius + Math.sin(time * 2 + particle.phase) * particle.amplitude * energyFactor * playingFactor;
        const x = centerX + Math.cos(particle.angle) * pulseRadius;
        const y = centerY + Math.sin(particle.angle) * pulseRadius;
        const size = particle.size * (0.5 + energyFactor * 0.5) * playingFactor;
        
        const colorMix = (Math.sin(particle.angle * 2 + time) + 1) / 2;
        const r = Math.floor(baseColor[0] * (1 - colorMix) + secondaryColor[0] * colorMix);
        const g = Math.floor(baseColor[1] * (1 - colorMix) + secondaryColor[1] * colorMix);
        const b = Math.floor(baseColor[2] * (1 - colorMix) + secondaryColor[2] * colorMix);
        
        ctx.shadowBlur = 10 * energyFactor;
        ctx.shadowColor = `rgb(${r}, ${g}, ${b})`;
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.6 + energyFactor * 0.4})`;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, Math.PI * 2);
        ctx.fill();
        
        if (energyFactor > 0.6 && i % 3 === 0) {
          const nextParticle = particlesRef.current[(i + 1) % particlesRef.current.length];
          const nextPulseRadius = nextParticle.radius + Math.sin(time * 2 + nextParticle.phase) * nextParticle.amplitude * energyFactor * playingFactor;
          const nextX = centerX + Math.cos(nextParticle.angle) * nextPulseRadius;
          const nextY = centerY + Math.sin(nextParticle.angle) * nextPulseRadius;
          
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${0.2 * energyFactor})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(nextX, nextY);
          ctx.stroke();
        }
      });
      
      // Wave rings
      for (let i = 0; i < 3; i++) {
        const waveRadius = 90 + i * 20 + Math.sin(time + i) * 10 * energyFactor;
        ctx.strokeStyle = `rgba(${baseColor.join(',')}, ${(0.3 - i * 0.1) * energyFactor * playingFactor})`;
        ctx.lineWidth = 2 - i * 0.5;
        ctx.beginPath();
        ctx.arc(centerX, centerY, waveRadius, 0, Math.PI * 2);
        ctx.stroke();
      }
      
      ctx.shadowBlur = 0;
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [energyLevel, isPlaying, isLive]);

  return (
    <motion.div
      className="absolute inset-0 pointer-events-none flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <canvas ref={canvasRef} width={320} height={320} className="w-[320px] h-[320px]" data-testid="energy-visualizer" />
    </motion.div>
  );
};

export default EnergyVisualizer;
